import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetNotificationsQuery,
  useBulkMarkAsReadMutation,
} from "../../../../store/admin/notificationSlice";
import {
  isStaffSystemRole,
  staffCanManageRoles,
} from "../../../../utils/staffRoleModules";
import { useUser } from "../../../../hooks";

/**
 * Super Admin banner for unread security (role/permission) alerts.
 * Dismissible — marks those alerts read so the banner stays closed until new ones arrive.
 */
export default function SecurityReviewBanner() {
  const user = useUser();
  const navigate = useNavigate();
  const isSuperAdmin = isStaffSystemRole(user?.role);
  const [dismissed, setDismissed] = useState(false);
  const [bulkMarkAsRead, { isLoading: isDismissing }] =
    useBulkMarkAsReadMutation();

  const { data, refetch } = useGetNotificationsQuery(
    {
      page: 1,
      status: "unread",
      limit: 20,
      guard: "staff",
      type: "security",
    },
    { skip: !isSuperAdmin, refetchOnMountOrArgChange: true }
  );

  if (!isSuperAdmin || dismissed) return null;

  const notifications = data?.notifications || [];
  if (!notifications.length) return null;

  const count = notifications.length;
  const latest = notifications[0];

  const handleDismiss = async () => {
    setDismissed(true);
    const ids = notifications.map((n) => n?.id).filter(Boolean);
    if (!ids.length) return;
    try {
      await bulkMarkAsRead({ ids, guard: "staff" }).unwrap();
      refetch();
    } catch {
      // Banner already hidden for this session; unread may reappear on next visit.
    }
  };

  return (
    <div
      className="mb-4 p-3 p-md-4 rounded-[12px] relative"
      style={{
        backgroundColor: "rgba(255, 149, 0, 0.12)",
        border: "1px solid #ff9500",
      }}
      role="alert"
    >
      <button
        type="button"
        className="absolute top-2 right-2 border-0 bg-transparent p-1 rounded-full hover:bg-black/5"
        aria-label="Dismiss security review banner"
        title="Dismiss"
        disabled={isDismissing}
        onClick={handleDismiss}
        style={{ lineHeight: 1, color: "#525252" }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="d-flex flex-column flex-md-row justify-content-between gap-3 align-items-md-center pe-4">
        <div>
          <p className="fw-semibold text-[#383838] mb-1">
            Security review needed
          </p>
          <p className="text-sm text-[#525252] mb-0">
            {count === 1
              ? latest?.title ||
                latest?.message ||
                "A role or permission change needs review."
              : `${count}+ unread role/permission alerts need your review.`}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#8b313a", borderRadius: 8 }}
            onClick={() => navigate("/super_admin/audit_log")}
          >
            Open Audit Log
          </button>
          {staffCanManageRoles(user?.role) ? (
            <button
              type="button"
              className="btn btn-sm"
              style={{
                backgroundColor: "#fff",
                border: "1px solid #a1a0a0",
                borderRadius: 8,
                color: "#383838",
              }}
              onClick={() =>
                navigate("/super_admin/settings/role_management")
              }
            >
              Role Management
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
